import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";

import { Badge } from "@/shared/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { QueryErrorBanner } from "@/shared/ui/QueryErrorBanner";
import { clickableCardProps } from "@/shared/ui/cardWall";
import { cn } from "@/shared/lib/utils";
import {
  characterService,
  realPersonEligibleOf,
  type CharacterDto,
  type CharacterMaterialDto,
} from "@/entities/character/api/characterService";
import { CharacterThumb } from "@/entities/character/ui/CharacterThumb";
import { errorText } from "@/shared/lib/apiErrors";

/**
 * 人物选择器(packs W4 消费端共用件,FSD entities 层 —— video/digital-human 两 feature 都要吃,
 * features 互不 import 故落 entities/character/ui)。
 *
 * 选中即解析消费 payload:主定妆图 objectKey(identity 槽,i2i 锚)+ appearance(subjects
 * 文本锁逐字用)。停用人物不出现;真人徽标照 W2 口径。
 */
export interface CharacterPick {
  character: CharacterDto;
  /** 主定妆图 objectKey(primaryMaterialId → 无则 identity 槽第一张 → 无 identity 物料则 null)。 */
  anchorKey: string | null;
  /** identity 槽全部物料 objectKey(主定妆图排最前,≤3):R2V 多图身份锁的数据源。
   *  库里 4 张角度图此前只消费主定妆图 1 张 —— 多图跨镜同人是 W1 实证的正确形态。 */
  identityKeys: string[];
  /**
   * identity 槽素材行(主定妆图排最前)。0915 归一批 B 追加:数字人线要的是**素材 id**
   * (avatarRef 现在指 xb_pack_material.id,形象表已退役),光有 objectKey 不够;
   * 而且要按 `kind` 分派管线(image→s2v/liveportrait,video→videoretalk)。
   */
  identityMaterials: CharacterMaterialDto[];
  /**
   * 分身口播可用素材(1006):dh_source 槽素材在前(主素材是 dh_source 时它排第一),
   * 再接 identity 素材 —— 分身线按 kind 从这里挑。真人出镜**不读**这一份。
   */
  dhMaterials: CharacterMaterialDto[];
}

/**
 * 选中一个人物 → 解析出 {@link CharacterPick}(锚图 + identity 多图集)。
 *
 * ★0915 车 F 从 `CharacterPickerBody.pick()` 抽出并导出:真人出镜专属页
 * (`VideoRealCreatePage`,第 1 步选出镜人)是**横向大卡**,不走这个弹框式选择器,
 * 但选中一个人物后要拿的东西(锚图 objectKey + identity 多图集)逐字一样 ——
 * 两处各写一遍就是「同一个知识两个出处」,选错哪张当主图的判据会漂。
 *
 * 列表 DTO 没有 objectKey(只有现签 primaryUrl)—— 消费端落库要 key,取详情解析。
 */
export async function resolveCharacterPick(c: CharacterDto): Promise<CharacterPick> {
  const detail = await characterService.get(c.id);
  const materials = detail.materials ?? [];
  // ★1006:主素材若是 dh_source(分身仓上传的口播素材)不当真人出镜锚 —— 回落 identity 第一张。
  const primary = materials.find(
    (m) => m.id === detail.primaryMaterialId && m.slot !== "dh_source"
  );
  const anchor = primary ?? materials.find((m) => m.slot === "identity") ?? null;
  const identityKeys = [
    ...(anchor?.objectKey ? [anchor.objectKey] : []),
    ...materials
      .filter((m) => m.slot === "identity" && m.objectKey && m.objectKey !== anchor?.objectKey)
      .map((m) => m.objectKey),
  ].slice(0, 3); // ai 段级 identity 配额 4,留 1 给手工/产品侧
  const identityMaterials = [
    ...(anchor ? [anchor] : []),
    ...materials.filter((m) => m.slot === "identity" && m.id !== anchor?.id),
  ];
  // 1006 分身素材:dh_source 优先(主素材若是 dh_source 排最前),再接 identity 组;按 id 去重。
  const dhSource = materials.filter((m) => m.slot === "dh_source");
  const dhOrdered = [
    ...dhSource.filter((m) => m.id === detail.primaryMaterialId),
    ...dhSource.filter((m) => m.id !== detail.primaryMaterialId),
    ...identityMaterials,
  ];
  const dhMaterials = dhOrdered.filter((m, i) => dhOrdered.findIndex((x) => x.id === m.id) === i);
  return {
    character: detail,
    anchorKey: anchor?.objectKey ?? null,
    identityKeys,
    identityMaterials,
    dhMaterials,
  };
}

/**
 * 真人人物在选择器里的多态(SEEDANCE 真人出镜 P0,0914 车 D4 → 0914 契约补齐追加 assets 态)。
 *
 * <p>判据与 `features/video/model/characterPick.ts` 的 `videoUsableCharacter` **同源**
 * (status 放行后还要 assets === "active" 才可选,两把尺——见该文件 `ASSETS_READY` 头注),
 * 但这里**不当 entities 层依赖 features 层**(FSD 边界:entities 只认 DTO 自身字段),所以
 * 在本文件内独立算一遍 —— 两处判据字面不同步是回归项,改一处记得改另一处(单测两边都盯着)。
 *
 * ★为什么不像老版本那样把「未就绪的真人」整个从列表里过滤掉(2026-08-13 拍板的旧判据):
 * 那样用户会看到「人物库没有可用人物」,却不知道库里明明有这个人、只是认证/入库没走完。
 * 现在**留在列表里,置灰 + 说明原因** —— 「有这个人,但现在不能选、为什么」。
 *
 * ★assets 判据在 status 放行之后才叠加(0914 追加):status 没过就按 status 那三态显示
 * (pending/failed/missing),不看 assets——用户先要解决"有没有认证过"这一层,素材是否
 * 入库完成是下一层的事,两层原因混在一起说不清楚该先做哪件事。
 */
export type RealPersonPickState =
  | "selectable"
  | "pending"
  | "failed"
  | "missing"
  | "assets_pending" // status 已放行,assets ∈ {none, registering}(未入库/入库中)
  | "assets_rejected" // status 已放行,assets = rejected(版权/公众人物)
  | "assets_failed"; // status 已放行,assets = failed(入库失败)

export function realPersonPickState(c: CharacterDto): RealPersonPickState {
  if (!c.isRealPerson) return "selectable";
  const status = c.verification?.status;
  if (status === "pending") return "pending";
  if (status === "failed") return "failed";
  if (status !== "not_required" && status !== "verified") {
    // verification 整块缺席/status 是未知值 = 老后端兼容态(DTO 头注)按未验证处理,不当免检。
    return "missing";
  }
  // status 放行后叠 assets(0914 契约补齐)—— P0 exempt 档下真正卡门槛的是这把尺。
  const assets = c.verification?.assets ?? "none";
  if (assets === "active") return "selectable";
  if (assets === "rejected") return "assets_rejected";
  if (assets === "failed") return "assets_failed";
  return "assets_pending"; // none | registering
}

/**
 * 置灰状态的原因文案(人话,§2.4 错误码同款措辞)。
 *
 * assets_* 三态(0914 契约补齐追加)文案逐字取自任务书 D4 原文 + §2.4 错误码人话:
 * `assets_pending`(素材未入库/入库中)对齐 `VENDOR_ASSET_NOT_READY`「人物素材准备中，
 * 稍后再试」;`assets_rejected` 对齐 `VENDOR_ASSET_REJECTED`「素材涉及版权或公众人物，
 * 无法生成」;`assets_failed` 是入库失败态,「素材准备失败」。
 */
export function realPersonPickReason(state: Exclude<RealPersonPickState, "selectable">): string {
  switch (state) {
    case "pending":
      // P1: status=pending 时此处挂「去认证」→ RealPersonVerifyDialog(按 verification.canStart 判可点)。
      return "认证进行中 —— 完成后即可选用。";
    case "failed":
      return "该人物认证未通过,暂不能用于真人出镜。";
    case "missing":
      return "还没拿到这个人的认证信息,暂不能选 —— 刷新页面再试。";
    case "assets_pending":
      return "人物素材准备中，稍后再试";
    case "assets_rejected":
      return "素材涉及版权或公众人物，无法生成";
    case "assets_failed":
      return "素材准备失败";
  }
}

/**
 * 选人**内容体**(2026-08-08 抽出):列表 + 「列表 DTO 没有 objectKey → 取详情解析锚图」那套。
 *
 * ★为什么要抽:分身舱选形象弹框把「按人物选」做成第四个平级 tab —— 它要的是这份内容,
 *   而不是再套一层 Dialog。弹框套弹框既难关,也说不清「关掉的是哪一层」。
 */
export function CharacterPickerBody({
  onSelect,
  realOnly = false,
  enabled = true,
  filter,
  emptyHint,
  gateRealPersonByVerification = false,
}: {
  onSelect: (pick: CharacterPick) => void;
  realOnly?: boolean;
  /** 挂在 Dialog 里时由 open 驱动;当作 tab 内容用时,挂载即取数。 */
  enabled?: boolean;
  /**
   * 消费端可用性筛子(2026-08-13):**点下去会当场被弹走的人物,不该出现在选择器里**。
   *
   * ★为什么筛子由调用方给:能不能用是**消费端**的规矩(分身舱看形象 kind 与本线是否相符,
   *   视频舱看的是别的),entities 层不认识那些规矩,写死在这里就是把 feature 的逻辑上提。
   */
  filter?: (c: CharacterDto) => boolean;
  /**
   * 筛完为空时的提示 —— 要说清**缺的是哪一样**,不然用户不知道去补什么。
   * ReactNode(而不是纯 string):cast=real 落地的空态要带一条真链接跳
   * `/characters/new-real`(D2),纯字符串放不下可点的东西。
   */
  emptyHint?: ReactNode;
  /**
   * 空态里那条「把分身舱的形象照升格为人物」补救路径是否给。
   *
   * ★升格红线是**照片**(后端 from-avatar-profile 拒 video 档)→ 只吃视频形象的消费端
   *   (分身舱视频线)给了也是白给:升格出来的人物挂的仍是照片形象,回到这个 tab 照样被筛掉。
   */
  /**
   * ★真人三态门(SEEDANCE 真人出镜 P0,0914 车 D4)—— **只给视频舱的消费端开**,默认 false。
   *
   * 这个组件是 entities 层共用件,分身舱「按人物选」(`DhAvatarPicker`)也在用它,但分身舱
   * 选真人人物跟 vapeur 真人出镜通道**完全无关**(分身走的是 avatarProfileId/voice,不走
   * verification 状态机)—— 开了这道门会把分身舱本来能点的真人人物也置灰,是一次误伤
   * (真机测试 `DigitalHumanCharacterPick.test.tsx` 三个真人流程用例抓住过这个回归)。
   * 只有视频舱的 `CharacterPanel` 传 `true`。
   */
  gateRealPersonByVerification?: boolean;
}) {
  const [resolvingId, setResolvingId] = useState<number | null>(null);
  const listQuery = useQuery({
    queryKey: ["characters"],
    queryFn: () => characterService.list(),
    enabled,
  });
  const items = (listQuery.data ?? []).filter(
    (c) =>
      c.status === "active" &&
      (!realOnly || c.isRealPerson) &&
      // ★1006:视频舱(真人出镜通道)不列分身仓建的人物 —— 它们只有分身口播素材,
      //   不是「认证没走完」的置灰态,是这条线根本不收,所以整条不列。分身舱不开此门。
      (!gateRealPersonByVerification || realPersonEligibleOf(c)) &&
      (!filter || filter(c))
  );

  /*
   * ★「从形象升格进人物库」这条空态捷径退役(0915 归一批 B):形象表并入素材表,
   * 升格口(from-avatar-profile)本身已经不存在 —— 它的两步链第一步没有了。
   * 空态现在只指一条路:去人物库建人物、在人物下传素材。
   */

  const pick = async (c: CharacterDto) => {
    setResolvingId(c.id);
    try {
      onSelect(await resolveCharacterPick(c));
    } finally {
      setResolvingId(null);
    }
  };

  return (
    <>
      <QueryErrorBanner
        isError={listQuery.isError}
        message={listQuery.error ? errorText(listQuery.error) : undefined}
        onRetry={() => listQuery.refetch()}
      />

      {listQuery.isLoading ? (
        <div
          className="grid grid-cols-3 gap-3 sm:grid-cols-4"
          data-testid="character-picker-skeleton"
        >
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="fx-skeleton aspect-[3/4] rounded-[calc(var(--radius)*1.5)] border border-border/50 bg-card/40"
            />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="py-8 text-center">
          <p className="text-sm text-muted-foreground">
            {emptyHint ??
              (realOnly
                ? "人物库里还没有真人人物 —— 去人物库点「新建人物」选真人建一位。"
                : "人物库还是空的 —— 去「资产 · 人物」建一位再回来。")}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
          {items.map((c) => {
            // D4(0914):真人三态 —— selectable 照旧点选;pending/failed/missing 置灰 + 说原因,
            // 不再像旧判据那样整条从列表里拿掉(见上面 realPersonPickState 头注)。
            // gateRealPersonByVerification=false 的消费端(分身舱)维持老行为:恒 selectable。
            const state = gateRealPersonByVerification ? realPersonPickState(c) : "selectable";
            const blocked = state !== "selectable";
            return (
              <div
                key={c.id}
                data-character-pick={c.id}
                data-real-person-state={c.isRealPerson ? state : undefined}
                aria-disabled={blocked || undefined}
                className={cn(
                  "overflow-hidden rounded-[calc(var(--radius)*1.5)] border bg-card transition-shadow",
                  blocked ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:shadow-md",
                  resolvingId === c.id && "opacity-60"
                )}
                {...(blocked ? {} : clickableCardProps(() => void pick(c)))}
              >
                <div className="aspect-[3/4] w-full bg-muted">
                  {/* 缩略优先 / 原图兜底 / 视频走 <video> —— 三条口径都在 CharacterThumb 里。 */}
                  <CharacterThumb character={c} />
                </div>
                <div className="space-y-0.5 px-2 py-1.5">
                  <div className="flex items-center gap-1.5">
                    <span className="min-w-0 flex-1 truncate text-xs font-medium">{c.name}</span>
                    {c.isRealPerson && (
                      <Badge
                        variant={blocked ? "outline" : "secondary"}
                        className="shrink-0 text-2xs"
                      >
                        {/* 「已授权」是 gate 开着(视频舱)才说的话:分身舱没有这层认证语义。 */}
                        {gateRealPersonByVerification && !blocked ? "真人 · 已授权" : "真人"}
                      </Badge>
                    )}
                  </div>
                  {blocked && (
                    <p
                      data-testid={`character-pick-blocked-${c.id}`}
                      className="text-2xs leading-snug text-muted-foreground"
                    >
                      {realPersonPickReason(state)}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

export function CharacterPickerDialog({
  open,
  onOpenChange,
  onSelect,
  /** 只列真人人物(分身出片场景:虚拟人物没有真人形象/声纹可挂)。 */
  realOnly = false,
  filter,
  emptyHint,
  title = "选人物",
  description = "从人物库选一位:主定妆图作画面锚,外观一句话逐字锁进提示词链。",
  gateRealPersonByVerification = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (pick: CharacterPick) => void;
  realOnly?: boolean;
  /** 消费端可用性筛子(见 `CharacterPickerBody`)。 */
  filter?: (c: CharacterDto) => boolean;
  emptyHint?: ReactNode;
  title?: string;
  description?: string;
  /** 真人三态门(见 `CharacterPickerBody` 头注)—— 只有视频舱传 true。 */
  gateRealPersonByVerification?: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <CharacterPickerBody
          realOnly={realOnly}
          enabled={open}
          filter={filter}
          emptyHint={emptyHint}
          gateRealPersonByVerification={gateRealPersonByVerification}
          // 升格出来的是**真人**人物(后端 insertRealPerson)——给了筛子的消费端多半正是不收真人的那些,
          // 升完还是选不中。有筛子就不给这条补救路径,免得走一趟空。
          onSelect={(pick) => {
            onSelect(pick);
            onOpenChange(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
